import { html, LitElement, nothing, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { FontSizes, FontWeights } from '../../../../../types/common/types';
import { translateFn } from '../../../../../utils/translator.helper';
import { renderSideMenuSectionTitle } from '../../../common/common';
import { BaseWebComponentTranslator } from '../../../../../framework/BaseWebComponentTranslator';
import { ComplianceStringToEnumMapping } from '../../../../common/constants';
import { GzTooltipWebComponent } from '../../../../../ui/gzTooltip';
import { GzLinkWebComponent } from '../../../../../ui/gzLinkComponent';
import { GzButtonWebComponent } from '../../../../../ui/gzButton';
import { GzDialog } from '../../../../../ui/gzDialog/component/GzDialog';
import { GzDialogWebComponent } from '../../../../../ui/gzDialog';

/**
 * @summary Type here the description of this component
 * @tag compliance-standards
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('compliance-standards')
export class ComplianceStandardsWebComponent extends LitElement {
  SECTIONS_LIMIT = 5;

  @property()
  indicatorId = null;

  @property()
  dialogContainer = null;

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  render() {
    if (!this.indicatorId) {
      return nothing;
    }

    const complianceMappings =
      Application.modules.riskManagement.controller.helpers.routinesTranslations.getDictionary('compliance');
    const indicatorCompliance = complianceMappings[this.indicatorId];

    if (!indicatorCompliance || Object.keys(indicatorCompliance).length === 0) {
      return nothing;
    }

    const complianceDetailsComponents: Array<TemplateResult> = [];

    for (const complianceName of Object.keys(indicatorCompliance)) {
      const complianceDetails =
        Application.modules.riskManagement.controller.helpers.routinesTranslations.getComplianceDetails(complianceName);
      if (
        !complianceDetails ||
        complianceDetails.length === 0 ||
        !indicatorCompliance[complianceName] ||
        indicatorCompliance[complianceName].length === 0
      ) {
        continue;
      }

      const controlSectionsMapping = this.buildControlMappingForSidePanel(
        indicatorCompliance[complianceName],
        complianceDetails
      );

      complianceDetailsComponents.push(
        this.getComplianceDetailsComponents(
          complianceName,
          controlSectionsMapping,
          indicatorCompliance,
          complianceDetails
        )
      );
    }

    if (complianceDetailsComponents.length === 0) {
      return nothing;
    }

    return html` <div>
      <gz-text fontSize="${FontSizes.PARAGRAPH_12}" class="mt-2"> ${translateFn('Compliance')}</gz-text>
      ${complianceDetailsComponents}
    </div>`;
  }

  private getLastSectionOfControl(control: any, sectionsIds: Array<string>): any {
    for (const sectionId of sectionsIds) {
      control = control['sections'][sectionId];
    }

    return control;
  }

  private buildControlMappingForSidePanel(paths: Array<string>, complianceDetails: any): any {
    const mapping = {} as any;

    for (const path of paths) {
      const [controlId, ...sectionsIds] = path;

      if (!mapping[controlId]) {
        mapping[controlId] = {
          name: complianceDetails[controlId]['name'],
          sections: [],
        };
      }

      mapping[controlId]['sections'].push({
        name: this.getLastSectionOfControl(complianceDetails[controlId], sectionsIds)['name'],
        id: path.at(-1),
      });
    }

    return mapping;
  }

  private getComplianceDetailsComponents(
    complianceName: string,
    controlSectionsMapping: any,
    indicatorCompliance: any,
    complianceDetails: any
  ): TemplateResult {
    const complianceControlsComponents: Array<TemplateResult> = [];

    for (const control of Object.values(controlSectionsMapping)) {
      if (!control) {
        continue;
      }

      complianceControlsComponents.push(
        html`
          <gz-labeled-content label="${control['name']}">
            ${this.getComplianceControlsComponents(
              control['sections'],
              indicatorCompliance[complianceName],
              complianceName,
              complianceDetails
            )}
          </gz-labeled-content>
        `
      );
    }

    return html`
      <div class="mt-1 pl-1 section-splitter">
        <div>
          ${renderSideMenuSectionTitle(
            BaseWebComponentTranslator.getInstance().getRiskManagementCompliance(
              ComplianceStringToEnumMapping[complianceName]
            )
          )}
          ${complianceControlsComponents}
        </div>
      </div>
    `;
  }

  private getComplianceControlsComponents(
    sections: Array<any>,
    paths: Array<string>,
    complianceName: string,
    complianceDetails: any
  ): TemplateResult {
    if (sections.length <= this.SECTIONS_LIMIT) {
      return html` ${this.getComplianceSectionsComponents(sections, paths, complianceName, complianceDetails)} `;
    }

    const subsections = sections.slice(0, this.SECTIONS_LIMIT);
    const complianceSectionsComponents = this.getComplianceSectionsComponents(
      subsections,
      paths,
      complianceName,
      complianceDetails
    );

    return html`
      ${complianceSectionsComponents}
      <gz-link
        class="mt-1 showMore"
        @click="${(e: Event) =>
          this.showAllComplianceSections(
            e,
            sections.slice(this.SECTIONS_LIMIT),
            paths,
            complianceName,
            complianceDetails
          )}"
      >
        ${translateFn('Show all')}
      </gz-link>
    `;
  }

  private getComplianceSectionsComponents(
    sections: Array<any>,
    paths: Array<string>,
    complianceName: string,
    complianceDetails: any
  ): Array<HTMLElement> {
    const elements: Array<HTMLElement> = [];
    for (const section of sections) {
      const name = section['id'] + " " + section['name'];
      const div = document.createElement('div');
      div.className = 'd-flex flex-row align-items-center compliance-element';
      const tooltip = new GzTooltipWebComponent();
      tooltip.tooltipText = name;
      tooltip.isEllipsis = true;
      const link = new GzLinkWebComponent();
      link.className = 'd-inline compliance-link';
      link.innerHTML = name;
      link.addEventListener('click', _ => {
        this.handleComplianceStandardClick(section['id'], paths, complianceName, complianceDetails);
      });

      tooltip.append(link);
      div.append(tooltip);

      elements.push(div);
    }

    return elements;
  }

  private handleComplianceStandardClick(
    sectionId: string,
    paths: Array<string>,
    complianceName: string,
    complianceControls: any
  ) {
    const path = paths.find(item => item.at(-1) === sectionId) || [];
    const [controlId, ...sectionsIds] = path;

    const dialogBody = document.createElement('div');
    const result = [
      `<gz-text fontSize="${FontSizes.HEADING_6}">${complianceControls[controlId]['description'] ?? ''}</gz-text>`,
    ];

    dialogBody.className = 'dialogBody mt-6 mb-6 ml-6 mr-6 w-60';
    dialogBody.innerHTML = [
      ...result,
      ...this.getSectionsDetailsElements(complianceControls[controlId], sectionsIds),
    ].join('');

    const dialogFooter = document.createElement('div');
    dialogFooter.style.cssText += 'justify-content: end;';
    const cancelButton = new GzButtonWebComponent();
    cancelButton.innerText = translateFn('Cancel');
    cancelButton.setAttribute('type', 'outline');
    cancelButton.setAttribute('color', 'secondary');
    dialogFooter.appendChild(cancelButton);

    const dialogTitle = `${BaseWebComponentTranslator.getInstance().getRiskManagementCompliance(
      ComplianceStringToEnumMapping[complianceName]
    )}: ${complianceControls[controlId]['name']}`;

    const dialogConfigs: GzDialog = {
      headline: dialogTitle,
      bodyContent: dialogBody,
      footerContent: dialogFooter,
      isOpen: true,
      isWindow: true,
      closableOutsideBox: false,
    };

    const dialog = GzDialogWebComponent.buildDialogElement(dialogConfigs, this.dialogContainer || undefined);

    cancelButton.addEventListener('click', () => {
      dialog.close();
    });
  }

  private getSectionsDetailsElements(control: any, sectionsIds: Array<string>): Array<string> {
    const elements: Array<string> = [];
    for (const sectionId of sectionsIds) {
      control = control['sections'][sectionId];
      elements.push(
        `
         <gz-text fontWeight="${FontWeights.BOLD}" fontSize="${FontSizes.HEADING_6}" class="mt-2 mb-2">
            ${sectionId} ${control['name']}
         </gz-text>
          <gz-text fontSize="${FontSizes.HEADING_6}">${control['description'] ?? ''}</gz-text>
        `
      );
    }

    return elements;
  }

  private showAllComplianceSections(
    event: any,
    sections: any,
    paths: Array<string>,
    complianceName: string,
    complianceDetails: any
  ) {
    const parent = event.target.parentElement;
    event.target.remove(); // remove show all button
    parent.append(...this.getComplianceSectionsComponents(sections, paths, complianceName, complianceDetails));
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'compliance-standards': ComplianceStandardsWebComponent;
  }
}
